import type { ResolvedSection } from '@/shell/model/resolve-navigation';
import type { ShellTranslate } from '@/shell/model/module-catalog';
import {
  placeKey,
  sectionPath,
  type AdministrationScope,
  type PlaceDetail,
  type SectionId,
} from './place';
import { placeFromPath } from './place-from-path';
import { canAt, sectionsFor } from './scopes';

const LABEL_KEYS = {
  overview: 'nav.adminSections.overview',
  tenants: 'nav.adminSections.tenants',
  organizations: 'nav.adminSections.organizations',
  people: 'nav.adminSections.people',
  roles: 'nav.adminSections.roles',
  invitations: 'nav.adminSections.invitations',
  modules: 'nav.adminSections.modules',
  'role-catalog': 'nav.adminSections.roleCatalog',
} as const satisfies Record<SectionId, Parameters<ShellTranslate>[0]>;

/**
 * The administration area's rail sections for the current address, or `undefined` outside the new
 * area — where the admin entry keeps its catalog sections, none, and today's operator screens are
 * left exactly as they are (FR-AD-12).
 *
 * The effective `can` is the place read's when the client has it, and otherwise what the grants
 * alone prove. When neither proves anything yet, the place shows no sections rather than a guess.
 */
export function adminSectionsFor(
  pathname: string,
  scopes: readonly AdministrationScope[],
  places: Readonly<Record<string, PlaceDetail>>,
  translate: ShellTranslate,
): readonly ResolvedSection[] | undefined {
  const located = placeFromPath(pathname);
  if (located === null) {
    return undefined;
  }
  const { place } = located;
  const detail = places[placeKey(place)];
  const can = detail?.can ?? canAt(place, scopes, detail?.tenant?.id ?? null);
  if (can === null) {
    return [];
  }
  return sectionsFor(place, can).map((id) => ({
    id,
    label: translate(LABEL_KEYS[id]),
    path: sectionPath(place, id),
    end: false,
  }));
}
